import type { Session } from "../auth/session.js";
import type { CartManager, CartSnapshot } from "./CartManager.js";
import type { Shop } from "@mall-mart/shared";

/**
 * Cross-device cart persistence for signed-in shoppers: restores the saved
 * snapshot after login and debounce-saves every cart change to PUT /api/cart
 * (which stores it on the shopper's Shopify customer record). Guests are
 * never synced.
 */

const SAVE_DEBOUNCE_MS: number = 2_000;

export async function restorePersistentCart(
  serverHttpUrl: string,
  session: Session,
  carts: CartManager,
  shops: Shop[]
): Promise<void> {
  // Credentials go in headers, not the query string (tokens must never hit
  // access logs).
  const res: Response = await fetch(`${serverHttpUrl}/api/cart`, {
    headers: {
      "x-user-email": session.email,
      authorization: `Bearer ${session.token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`GET /api/cart failed: ${res.status}`);
  }
  const data = (await res.json()) as { cart: CartSnapshot };
  carts.restore(data.cart, new Map(shops.map((s: Shop) => [s.id, s])));
}

/** Wire debounced saves; returns the change handler's cancel function. */
export function startCartAutosave(
  serverHttpUrl: string,
  carts: CartManager,
  onAuthExpired: () => void
): () => void {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const handler = (): void => {
    const session: Session | null = carts.currentSession();
    if (session === null) {
      return; // guest: nothing to persist
    }
    if (timer !== undefined) {
      clearTimeout(timer);
    }
    timer = setTimeout(() => {
      void saveNow(serverHttpUrl, carts, onAuthExpired);
    }, SAVE_DEBOUNCE_MS);
  };
  carts.onChange(handler);
  return () => {
    if (timer !== undefined) {
      clearTimeout(timer);
    }
  };
}

async function saveNow(
  serverHttpUrl: string,
  carts: CartManager,
  onAuthExpired: () => void
): Promise<void> {
  const session: Session | null = carts.currentSession();
  if (session === null) {
    return;
  }
  try {
    const res: Response = await fetch(`${serverHttpUrl}/api/cart`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: session.email,
        token: session.token,
        cart: carts.snapshot(),
      }),
    });
    if (res.status === 401) {
      onAuthExpired();
      return;
    }
    if (!res.ok) {
      console.warn(`[Mall Mart] Cart save failed: ${res.status}`);
    }
  } catch (err: unknown) {
    console.warn("[Mall Mart] Cart save failed:", err);
  }
}
